<script setup lang="ts">
/**
 * 源站 .header_container：未登录 login_btn+reg_btn；已登录双余额（现金+星币）/宝箱/头像。
 * 450：高 72 / pad 0 12 / bg #232325 / 登录 #FB9308 / 注册 38.4 #EE1E40。
 * 币标旁金额不加 `$`，与源站 ChangeHuobi(..., " ") 一致。
 * 金额框按现金/积分较长一侧撑开，不再用源站 6.58667rem 固定宽。
 */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { useOroMxPlayerIdentity } from '@/features/profile/use-oro-mx-player-identity';
import { t } from '@/shared/i18n/messages';
import { oroMxCurrencyAssets } from '@/shared/theme/oro-mx-currency-assets';
import { oroMxAssetPaths } from '@/shared/theme/oro-mx-home-data';
import { formatOroMxIntegerAmount } from '@/shared/theme/oro-mx-money';
import { requestGuestBindPrompt } from '@/features/auth/guest-bind-prompt';
import { useAuthModalStore } from '@/stores/auth-modal-store';
import { useWalletStore } from '@/stores/wallet-store';
import OroMxRegisterGiftTip from '@/widgets/app-shell/OroMxRegisterGiftTip.vue';

const { avatarSrc, sessionStore } = useOroMxPlayerIdentity();
const walletStore = useWalletStore();
const authModal = useAuthModalStore();

const cashLabel = computed(() => {
  if (!sessionStore.isAuthenticated || !walletStore.loaded) return '0';
  return formatOroMxIntegerAmount(walletStore.availableMinor);
});

const pointsLabel = computed(() => {
  if (!sessionStore.isAuthenticated || !walletStore.loaded) return '0';
  return formatOroMxIntegerAmount(walletStore.pointsMinor);
});

</script>

<template>
  <div class="header_container">
    <RouterLink class="logo_wrap" to="/" aria-label="Casino life">
      <img class="logo_pic" :src="oroMxAssetPaths.logo" alt="Casino life" />
    </RouterLink>
    <div v-if="sessionStore.isAuthenticated" class="wallet_user_wrap">
      <div class="wallet_info">
        <div class="wallet_balances">
          <div class="wallet_balance_item">
            <img class="wallet_coin" :src="oroMxCurrencyAssets.cash" alt="" />
            <span>{{ cashLabel }}</span>
          </div>
          <div class="wallet_balance_item">
            <img class="wallet_coin" :src="oroMxCurrencyAssets.bonus" alt="" />
            <span>{{ pointsLabel }}</span>
          </div>
        </div>
        <RouterLink class="wallet_btn" to="/deposit" :aria-label="t('oro.header.deposit')">
          <svg class="wallet_btn__icon" viewBox="0 0 44 36" aria-hidden="true">
            <use :href="`${oroMxAssetPaths.sprite}#icon-wallet`" />
          </svg>
        </RouterLink>
      </div>
      <RouterLink class="discount_btn" to="/discount" :aria-label="t('oro.header.redeem')">
        <img class="btn_img" :src="oroMxAssetPaths.treasureChest" alt="" />
      </RouterLink>
      <RouterLink class="header_user_avatar" to="/account" :aria-label="t('oro.header.profile')" @click="requestGuestBindPrompt">
        <img class="header_user_avatar_img" :src="avatarSrc" alt="" />
      </RouterLink>
    </div>
    <div v-else class="header_guest">
      <button class="login_btn" type="button" @click="authModal.openAuth({ mode: 'login' })">
        {{ t('oro.header.login') }}
      </button>
      <div class="header_reg_wrap">
        <button class="red_button reg_btn" type="button" @click="authModal.openAuth({ mode: 'register' })">
          {{ t('oro.header.register') }}
        </button>
        <OroMxRegisterGiftTip variant="header" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.header_container {
  z-index: 10;
  display: flex;
  overflow: visible;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  height: 3.2rem;
  padding: 0 0.5333rem;
  background: #232325;
}

.header_guest {
  display: flex;
  overflow: visible;
  align-items: center;
}

.header_reg_wrap {
  position: relative;
  overflow: visible;
}

.logo_wrap,
.logo_pic {
  display: block;
  flex-shrink: 0;
  width: 5.2rem;
  height: 1.76rem;
  object-fit: contain;
}

.login_btn {
  margin-right: 1rem;
  padding: 0;
  color: #fb9308;
  font-size: 0.8rem;
  line-height: 1.2;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.red_button.reg_btn {
  height: 1.70667rem;
  padding: 0 0.64rem;
  color: #fff;
  font-size: 0.8rem;
  line-height: 1.70667rem;
  background: #ee1e40;
  border: 0;
  border-radius: 0.37333rem;
  cursor: pointer;
}

.wallet_user_wrap {
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-end;
  gap: 0.64rem;
  align-items: center;
  min-width: 0;
}

.wallet_info {
  display: flex;
  flex: 0 1 auto;
  overflow: hidden;
  align-items: center;
  width: max-content;
  max-width: 100%;
  min-width: 0;
  height: 2.2933rem;
  background: #2e2e36;
  border-radius: 0.16rem;
}

.wallet_balances {
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  width: max-content;
  max-width: calc(100% - 2.2933rem);
  min-width: 0;
  padding: 0.12rem 0.32rem;
  gap: 0.04rem;
}

.wallet_balance_item {
  display: flex;
  width: max-content;
  max-width: 100%;
  min-width: 0;
  align-items: center;
  gap: 0.16rem;
  color: #fff;
  font-size: 0.64rem;
  font-weight: 400;
  line-height: 0.96rem;
}

.wallet_balance_item span {
  overflow: hidden;
  min-width: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wallet_coin {
  display: block;
  flex-shrink: 0;
  width: 0.8rem;
  height: 0.8rem;
  object-fit: contain;
}

.wallet_btn {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.2933rem;
  height: 2.2933rem;
  padding: 0;
  background: #ee1e40;
  border: 0;
  border-radius: 0.16rem;
  cursor: pointer;
}

.wallet_btn__icon {
  width: 0.8rem;
  height: 0.9778rem;
}

.discount_btn {
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.10667rem;
  height: 2.29333rem;
  background: linear-gradient(180deg, #232325 8.48%, #640c20 94.2%);
  border-radius: 0.21333rem;
}

.btn_img {
  display: block;
  width: auto;
  height: 1.04rem;
  object-fit: contain;
  transform-origin: 50% 85%;
  animation: discount-treasure-sway 2.2s ease-in-out infinite;
}

@keyframes discount-treasure-sway {
  0%,
  100% {
    transform: rotate(0deg);
  }

  15% {
    transform: rotate(-12deg);
  }

  30% {
    transform: rotate(12deg);
  }

  45% {
    transform: rotate(-8deg);
  }

  60% {
    transform: rotate(8deg);
  }

  75% {
    transform: rotate(0deg);
  }
}

.header_user_avatar,
.header_user_avatar_img {
  display: block;
  flex-shrink: 0;
  width: 2.2933rem;
  height: 2.2933rem;
  border-radius: 50%;
  object-fit: cover;
}
</style>
